import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster } from "../../Components/ui/sonner";
import { toast } from "sonner";
import videoIcon from "../../Assets/icons/video.svg";
import microphoneIcon from "../../Assets/icons/microphone.svg";
import pencilIcon from "../../Assets/icons/pencil.svg";
import uploadIcon from "../../Assets/icons/upload.svg";
import arrowLeftIcon from "../../Assets/icons/arrow-left.svg";
import okIcon from "../../Assets/icons/ok.svg";
import recordingIcon from "../../Assets/icons/recording.svg";
import shareIcon from "../../Assets/icons/share.svg";
import dragIcon from "../../Assets/icons/drag-with-finger.svg";
import giftIcon from "../../Assets/icons/gift.svg";

// Video Preview Component to properly handle blob URLs
const VideoPreview = ({ file }) => {
  const videoRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(null);

  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setVideoUrl(url);

      return () => {
        // Clean up the blob URL when component unmounts or file changes
        URL.revokeObjectURL(url);
      };
    }
  }, [file]);

  useEffect(() => {
    if (videoRef.current && videoUrl) {
      videoRef.current.load(); // Force reload the video
    }
  }, [videoUrl]);

  if (!videoUrl) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-black text-white">
        Loading video...
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      src={videoUrl}
      controls
      autoPlay
      playsInline
      className="w-full h-full object-cover"
      onError={(e) => {
        console.error("Video playback error:", e);
      }}
    />
  );
};

const GiftUpload = () => {
  const { giftId } = useParams();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(window.location.search);
  const isOnboarding = queryParams.get("onboarding") === "true";

  const [step, setStep] = useState(isOnboarding ? "select" : "intro"); // intro, select, record, kindness, success
  const [type, setType] = useState(null); // video, audio, text, upload
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [senderName, setSenderName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [isActivated, setIsActivated] = useState(true);
  const [isGlobalMode, setIsGlobalMode] = useState(false); // Track if we are recording for global pool
  const [showDrawer, setShowDrawer] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [giftSession, setGiftSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);

  const mediaRecorderRef = useRef(null);
  const videoRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [mediaBlob, setMediaBlob] = useState(null);
  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        setLoadingSession(true);
        const res = await fetch(
          `${process.env.REACT_APP_PUBLIC_BACKEND_URL}get_gift_session`,
          {
            method: "POST",
            body: JSON.stringify({ giftId }),
          }
        );
        const data = await res.json();
        if (data) {
          setGiftSession(data);
          setIsActivated(data.activated);
        }
      } catch (e) {
        console.error("Error fetching session", e);
        toast.error("Failed to load gift information");
      } finally {
        setLoadingSession(false);
      }
    };
    fetchSession();
  }, [giftId]);

  // Show scroll hint when file is uploaded, hide after 3 seconds
  useEffect(() => {
    if (file && (type === "video" || type === "audio" || type === "image")) {
      setShowScrollHint(true);
      const timer = setTimeout(() => {
        setShowScrollHint(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [file, type]);

  // Helper to find supported mime type
  const getSupportedMimeType = (mediaType) => {
    const types =
      mediaType === "video"
        ? [
            "video/mp4",
            "video/webm;codecs=vp9,opus",
            "video/webm;codecs=vp8,opus",
            "video/webm",
          ]
        : ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return ""; // Let browser use default
  };

  // Initialize stream when entering record step
  useEffect(() => {
    let currentStream = null;

    const initStream = async () => {
      // Only init stream if we are in record step, have a type, AND have no file (meaning we are recording)
      if (
        step === "record" &&
        (type === "video" || type === "audio") &&
        !file
      ) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: type === "video",
          });
          currentStream = stream;

          if (videoRef.current && type === "video") {
            videoRef.current.srcObject = stream;
          }
        } catch (err) {
          console.error("Error accessing media devices:", err);
          toast.error("Could not access camera/microphone");
        }
      }
    };

    initStream();

    return () => {
      if (currentStream) {
        currentStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [step, type, file]);

  const startRecording = async () => {
    try {
      // Use existing stream from videoRef or get new one if needed
      let stream = videoRef.current?.srcObject;

      if (!stream || !stream.active) {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: type === "video",
        });
        if (videoRef.current && type === "video") {
          videoRef.current.srcObject = stream;
        }
      }

      const mimeType = getSupportedMimeType(type);
      const options = mimeType ? { mimeType } : {};

      mediaRecorderRef.current = new MediaRecorder(stream, options);

      // Store the actual mime type being used
      const actualMimeType =
        mediaRecorderRef.current.mimeType ||
        mimeType ||
        (type === "video" ? "video/webm" : "audio/webm");

      const chunks = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      mediaRecorderRef.current.onstop = () => {
        // Ensure we have chunks before creating blob
        if (chunks.length > 0) {
          const blob = new Blob(chunks, { type: actualMimeType });
          setMediaBlob(blob);

          // Safely extract extension or use default
          let ext = type === "video" ? "mp4" : "webm"; // Default based on likely support
          if (actualMimeType && actualMimeType.includes("/")) {
            const parts = actualMimeType.split("/");
            if (parts[1]) {
              // Handle complex types like 'video/webm;codecs=vp9'
              ext = parts[1].split(";")[0];
            }
          }
          const file = new File([blob], `recording.${ext}`, {
            type: actualMimeType,
          });
          setFile(file);
        } else {
          console.error("No chunks available to create blob");
        }
      };

      // Request data periodically to ensure we capture all chunks
      mediaRecorderRef.current.start(1000); // Request data every 1s
      setRecording(true);
      setTimer(0);
      timerRef.current = setInterval(() => {
        setTimer((t) => {
          if (t >= 90) {
            // 90 seconds limit
            stopRecording();
            toast.warning("Maximum recording time reached (90s)");
            return t;
          }
          return t + 1;
        });
      }, 1000);
    } catch (err) {
      console.error("Error accessing media devices:", err);
      toast.error("Could not access camera/microphone");
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      // Request final data before stopping
      if (mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.requestData();
      }

      mediaRecorderRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);

      // Stop all tracks
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream
          .getTracks()
          .forEach((track) => track.stop());
      }

      // Clear the video srcObject to stop showing the live stream
      if (videoRef.current) {
        videoRef.current.srcObject = null;
        videoRef.current.src = "";
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile) {
      // 50MB Limit
      if (uploadedFile.size > 50 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 50MB.");
        return;
      }

      // Strict Type Check
      if (!uploadedFile.type.match(/^(image|video|audio)\//)) {
        toast.error("Only images, videos, and audio files are allowed.");
        return;
      }

      setFile(uploadedFile);
      // Determine type based on file
      if (uploadedFile.type.startsWith("video")) setType("video");
      else if (uploadedFile.type.startsWith("audio")) setType("audio");
      else if (uploadedFile.type.startsWith("image")) setType("image");
    }
  };

  const handleUpload = async (isGlobal = false) => {
    setUploading(true);
    try {
      let contentUrl = "";

      if (type !== "text" && file) {
        // 1. Get presigned URL
        const presignedRes = await fetch(
          `${process.env.REACT_APP_PUBLIC_BACKEND_URL}get_presigned_url`,
          {
            method: "POST",
            body: JSON.stringify({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
            }),
          }
        );
        const presignedData = await presignedRes.json();

        // 2. Upload to S3
        await fetch(presignedData.presignedUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });

        contentUrl = `https://sayhelloo-files-672322547979.s3.amazonaws.com/${presignedData.key}`;
      }

      // 3. Save message
      await fetch(
        `${process.env.REACT_APP_PUBLIC_BACKEND_URL}upload_gift_message`,
        {
          method: "POST",
          body: JSON.stringify({
            giftId: giftId, // Always pass giftId to track origin
            type: type === "upload" ? "video" : type,
            contentUrl,
            textContent: content,
            senderName,
            isGlobal: isGlobalMode,
          }),
        }
      );

      // Auto-activate (only for the main gift)
      if (!isActivated && !isGlobalMode) {
        await fetch(
          `${process.env.REACT_APP_PUBLIC_BACKEND_URL}activate_gift`,
          {
            method: "POST",
            body: JSON.stringify({ giftId }),
          }
        );
        setIsActivated(true);
      }

      // Flow: record message -> kindness (can skip) -> success (with share link)
      if (!isGlobalMode) {
        setStep("kindness");
      } else {
        setStep("success");
      }
      setShowDrawer(false);
    } catch (error) {
      console.error("Error uploading:", error);
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const startGlobalFlow = () => {
    setIsGlobalMode(true);
    setFile(null);
    setContent("");
    setMediaBlob(null);
    setStep("select");
  };

  // --- UI COMPONENTS ---

  const SelectionCard = ({ icon, label, onClick, color }) => (
    <motion.button
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center gap-4 w-full aspect-square justify-center hover:shadow-md transition-all"
    >
      <div
        className={`w-12 h-12 rounded-full ${color} flex items-center justify-center`}
      >
        <img src={icon} alt={label} className="w-6 h-6" />
      </div>
      <span className="font-medium text-gray-700">{label}</span>
    </motion.button>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-gray-900 font-sans selection:bg-gray-200">
      <Toaster position="top-center" richColors />
      <div className="max-w-md mx-auto min-h-screen flex flex-col p-2">
        {/* Header */}
        <header className="flex justify-between items-center mb-1 pt-1">
          <button
            onClick={() => {
              if (step === "intro") {
                navigate(-1);
              } else if (step === "select") {
                setStep("intro");
              } else {
                setStep("select");
              }
            }}
            className="text-gray-400 hover:text-black transition-colors p-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>
          </button>
        </header>

        <AnimatePresence mode="wait">
          {step === "intro" && (
            <motion.div
              key="intro"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex-1 flex flex-col justify-center items-center p-6"
            >
              {loadingSession ? (
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-500">Loading...</p>
                </div>
              ) : giftSession ? (
                <div className="w-full max-w-sm space-y-12 text-center">
                  {/* Gift Icon */}
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
                    className="flex justify-center"
                  >
                    <div className="w-40 h-40 rounded-3xl bg-gray-50 flex items-center justify-center">
                      <img
                        src={giftIcon}
                        alt="Gift"
                        className="w-32 h-32 opacity-80"
                      />
                    </div>
                  </motion.div>

                  {/* Content */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                    className="space-y-4"
                  >
                    <h1 className="text-3xl font-bold text-gray-900">
                      For {giftSession.recipientName || "Someone Special"}
                    </h1>
                    <p className="text-lg text-gray-500">
                      {giftSession.occasion || "Special Occasion"}
                    </p>
                    <p className="text-base text-gray-400">Leave a message</p>
                  </motion.div>

                  {/* CTA Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.5 }}
                    className="pt-4"
                  >
                    <button
                      onClick={() => setStep("select")}
                      className="w-full bg-black text-white font-medium py-4 px-6 rounded-xl hover:bg-gray-800 transition-colors"
                    >
                      Continue
                    </button>
                  </motion.div>
                </div>
              ) : (
                <div className="text-center">
                  <p className="text-gray-500 mb-4">Gift not found</p>
                  <button
                    onClick={() => navigate(-1)}
                    className="text-gray-400 hover:text-black transition-colors"
                  >
                    Go back
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {step === "select" && (
            <motion.div
              key="select"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex-1 flex flex-col justify-start"
            >
              <div className="flex flex-col items-start p-4 justify-start">
                <h1 className="text-3xl font-bold mb-2 tracking-tight">
                  {isGlobalMode ? "Share Kindness" : "Add your message"}
                </h1>
                <p className="text-gray-500 mb-8">
                  {isGlobalMode
                    ? "Choose how you'd like to send a message to a stranger."
                    : isOnboarding
                    ? "Record the first message to get the ball rolling!"
                    : "Choose how you'd like to contribute."}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <SelectionCard
                  icon={videoIcon}
                  label="Video"
                  onClick={() => {
                    setType("video");
                    setStep("record");
                  }}
                  color="bg-blue-50 text-blue-600"
                />
                <SelectionCard
                  icon={microphoneIcon}
                  label="Audio"
                  onClick={() => {
                    setType("audio");
                    setStep("record");
                  }}
                  color="bg-purple-50 text-purple-600"
                />
                <SelectionCard
                  icon={pencilIcon}
                  label="Text"
                  onClick={() => {
                    setType("text");
                    setStep("record");
                  }}
                  color="bg-green-50 text-green-600"
                />
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*,video/*,audio/*"
                    className="hidden"
                    onChange={(e) => {
                      handleFileUpload(e);
                      setStep("record");
                    }}
                  />
                  <motion.div
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center gap-4 w-full aspect-square justify-center hover:shadow-md transition-all h-full"
                  >
                    <div className="w-12 h-12 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center">
                      <img src={uploadIcon} alt="Upload" className="w-6 h-6" />
                    </div>
                    <span className="font-medium text-gray-700">Upload</span>
                  </motion.div>
                </label>
              </div>

              {isOnboarding && (
                <div className="mt-8 text-center">
                  <button
                    onClick={() => {
                      // During onboarding, skip goes to success to show share link
                      setStep("success");
                    }}
                    className="text-gray-400 font-medium hover:text-gray-600 transition-colors"
                  >
                    Skip for now
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {step === "record" && (
            <motion.div
              key="record"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              className="flex-1 flex flex-col"
            >
              {(type === "video" || type === "audio") && !file ? (
                <div className="flex-1 flex flex-col items-center justify-start pt-4">
                  <div className="w-full aspect-[9/16] bg-black rounded-2xl overflow-hidden relative mb-2 shadow-xl">
                    {type === "video" && (
                      <video
                        ref={videoRef}
                        autoPlay
                        muted
                        playsInline
                        className="w-full h-full object-cover"
                      />
                    )}
                    {type === "audio" && (
                      <div className="w-full h-full flex items-center justify-center bg-gray-900">
                        <motion.div
                          animate={
                            recording ? { scale: [1, 1.2, 1] } : { scale: 1 }
                          }
                          transition={{ repeat: Infinity, duration: 1.5 }}
                          className="w-24 h-24 rounded-full bg-purple-500/20 flex items-center justify-center"
                        >
                          <img
                            src={microphoneIcon}
                            alt="Audio"
                            className="w-10 h-10 invert opacity-80"
                          />
                        </motion.div>
                      </div>
                    )}

                    {recording && (
                      <div className="absolute top-4 right-4 bg-red-500/80 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-2 z-10">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                        {formatTime(timer)}
                      </div>
                    )}

                    {/* Controls Overlay */}
                    <div className="absolute bottom-8 left-0 right-0 flex justify-center items-center z-20">
                      {!recording ? (
                        <button
                          onClick={startRecording}
                          className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md border-4 border-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
                        >
                          <img
                            src={type === "video" ? videoIcon : microphoneIcon}
                            alt="Record"
                            className="w-8 h-8 opacity-100 invert"
                          />
                        </button>
                      ) : (
                        <button
                          onClick={stopRecording}
                          className="w-20 h-20 rounded-full bg-red-500 border-4 border-white shadow-lg flex items-center justify-center hover:scale-105 transition-transform"
                        >
                          <div className="w-8 h-8 bg-white rounded-md"></div>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : type === "text" ? (
                <div className="flex-1 flex flex-col">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">Write Message</h2>
                    <button
                      onClick={() => setShowDrawer(true)}
                      disabled={!content}
                      className="bg-black text-white font-bold py-2 px-6 rounded-xl hover:bg-gray-900 transition-colors shadow-lg text-sm disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="relative">
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Write something heartfelt..."
                      maxLength={500}
                      className="w-full bg-white border-none rounded-2xl px-6 py-6 mb-4 focus:ring-2 focus:ring-black min-h-[300px] text-lg shadow-sm resize-none"
                    />
                    <div className="absolute bottom-6 right-6 text-xs text-gray-400 font-medium">
                      {content.length}/500
                    </div>
                  </div>
                </div>
              ) : (
                // Preview for Upload
                <div className="flex-1 flex flex-col">
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-bold">Preview Upload</h2>
                    <button
                      onClick={() => setShowDrawer(true)}
                      className="bg-black text-white font-bold py-2 px-6 rounded-xl hover:bg-gray-900 transition-colors shadow-lg text-sm"
                    >
                      Next
                    </button>
                  </div>
                  <div className="w-full aspect-[9/16] bg-gray-100 rounded-xl mb-6 flex items-center justify-center overflow-hidden">
                    {file?.type.startsWith("video") && (
                      <VideoPreview file={file} />
                    )}
                    {file?.type.startsWith("image") && (
                      <img
                        src={URL.createObjectURL(file)}
                        alt="Preview"
                        className="w-full h-full object-contain"
                      />
                    )}
                    {file?.type.startsWith("audio") && (
                      <div className="flex flex-col items-center gap-2">
                        <img
                          src={microphoneIcon}
                          alt="Audio"
                          className="w-12 h-12 opacity-50"
                        />
                        <span className="text-sm text-gray-500">
                          {file.name}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Caption Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add a caption (optional)
                    </label>
                    <div className="relative">
                      <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="Say something about this..."
                        maxLength={150}
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-black/5 focus:border-black min-h-[80px] text-base resize-none transition-all"
                      />
                      <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                        {content.length}/150
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center gap-4">
                    <button
                      onClick={() => {
                        setFile(null);
                        setMediaBlob(null);
                        setContent("");
                      }}
                      className="flex-1 px-6 py-3 rounded-xl text-red-500 font-medium bg-red-50 hover:bg-red-100 transition-colors"
                    >
                      Redo
                    </button>
                    <button
                      onClick={() => setShowDrawer(true)}
                      className="flex-1 bg-black text-white font-bold py-3 rounded-xl hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 shadow-lg"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Drawer for Details */}
          <AnimatePresence>
            {showDrawer && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onClick={() => setShowDrawer(false)}
                  className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
                />
                <motion.div
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "spring", damping: 25, stiffness: 200 }}
                  className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-12 z-50 shadow-2xl"
                >
                  <div className="w-12 h-1 bg-gray-200 rounded-full mx-auto mb-6" />

                  <h3 className="text-xl font-bold mb-4">Who is this from?</h3>

                  <input
                    type="text"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="Your Name (Optional)"
                    maxLength={50}
                    className="w-full bg-gray-100 border-none rounded-xl px-4 py-4 mb-4 focus:ring-2 focus:ring-black text-lg"
                    autoFocus
                  />

                  <button
                    onClick={() => handleUpload(isGlobalMode)}
                    disabled={uploading}
                    className="w-full bg-black text-white font-bold py-4 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-900 transition-colors flex items-center justify-center gap-2 text-lg"
                  >
                    {uploading
                      ? "Sending..."
                      : isGlobalMode
                      ? "Send Kindness"
                      : "Send"}
                    {!uploading && (
                      <img src={shareIcon} alt="" className="w-7 h-7 invert" />
                    )}
                  </button>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {step === "kindness" && (
            <motion.div
              key="kindness"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="flex-1 flex flex-col justify-center items-center p-6"
            >
              <div className="w-full max-w-md space-y-8">
                {/* Icon - subtle and minimal */}
                <div className="flex justify-center">
                  <div className="w-24 h-24 rounded-2xl bg-gray-50 flex items-center justify-center">
                    <img
                      src={recordingIcon}
                      alt="Recording"
                      className="w-12 h-12 opacity-70"
                    />
                  </div>
                </div>

                {/* Content - Notion-style typography */}
                <div className="space-y-3 text-center">
                  <h2 className="text-3xl font-semibold text-gray-900 tracking-tight">
                    Spread the Love?
                  </h2>
                  <p className="text-base text-gray-600 leading-relaxed max-w-sm mx-auto">
                    Would you like to record a separate message for a stranger
                    to brighten their day?
                  </p>
                </div>

                {/* Primary Action - Notion-style button */}
                <div className="space-y-4">
                  <button
                    onClick={startGlobalFlow}
                    disabled={uploading}
                    className="w-full bg-gray-900 text-white font-medium py-3.5 px-4 rounded-lg hover:bg-gray-800 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                  >
                    Yes, Share Kindness
                  </button>

                  {/* Skip option - subtle label */}
                  <div className="text-center">
                    <button
                      onClick={() => setStep("success")}
                      className="text-sm text-gray-400 hover:text-gray-600 transition-colors font-normal"
                    >
                      Keep it private
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex-1 flex flex-col items-center justify-center text-center p-6"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <img
                  src={okIcon}
                  alt="Success"
                  className="w-10 h-10 text-green-600"
                />
              </div>
              <h2 className="text-3xl font-bold mb-4">
                {isOnboarding ? "Gift Created!" : "Message Sent!"}
              </h2>
              <p className="text-gray-500 mb-8">
                {isGlobalMode
                  ? "Thank you for spreading kindness to the world."
                  : isOnboarding
                  ? "Your gift is ready. Share the link below to collect messages."
                  : "Your message has been added to the gift."}
              </p>

              {isOnboarding && !isGlobalMode && (
                <div className="w-full max-w-sm mb-8">
                  <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex items-center gap-3">
                    <div className="flex-1 overflow-hidden">
                      <p className="text-xs text-gray-400 mb-1">Share Link</p>
                      <p className="text-sm font-mono text-gray-600 truncate">
                        {`${window.location.origin}/gift/${giftId}/contribute`}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(
                          `${window.location.origin}/gift/${giftId}/contribute`
                        );
                        toast.success("Link copied!");
                      }}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-xs font-bold transition-colors"
                    >
                      Copy
                    </button>
                  </div>

                  <button
                    onClick={() => navigate(`/gift/${giftId}/dashboard`)}
                    className="w-full bg-black text-white font-bold py-4 rounded-xl hover:bg-gray-900 transition-colors shadow-lg mb-3"
                  >
                    Go to Dashboard
                  </button>
                </div>
              )}

              {!isOnboarding && (
                <button
                  onClick={() => window.location.reload()}
                  className="text-black font-medium hover:underline"
                >
                  Send another message
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Scroll Hint Overlay */}
        <AnimatePresence>
          {showScrollHint && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none"
            >
              <div className="bg-black/80 backdrop-blur-sm text-white px-6 py-4 rounded-2xl flex items-center gap-3 shadow-2xl">
                <img
                  src={dragIcon}
                  alt="Scroll"
                  className="w-6 h-6 rotate-90 invert"
                />
                <span className="text-base font-medium">
                  Scroll down to add a caption
                </span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default GiftUpload;
